import { IPublishState, NotificationPublishStatus, TableKeys, UniqueId } from '@communication-center/data-objects';
import _map from 'lodash/map';
import { notificationsListActions } from '../actionTypes';
import {
    getNotificationPublishState,
    isDraftNotification,
    isNotificationOld,
    isNotificationScheduled,
    Notification,
    setNotificationPublishState,
    TabsConfig
} from '../../models';
import { NotificationPauseState } from './common';
import { DrawerMode, IDrawer } from '../reducers/notificationsList';
import { ThunkAction } from '../../infrastructure/store.interface';
import { publishNotificationsConfirm } from '../../components/Dialogs/PublishNotificationConfirm/PublishNotificationConfirm';
import { enqueueSnackbar } from './ui';
import { confirmDelete } from '../../components/Dialogs/DeleteDialog/DeleteDialog';
import { confirmDialog } from '../../components/Dialogs/ConfirmDialog/ConfirmDialog';
import { triggerRowAnimation } from '../../components/AppTable/AppTable';
import { SNACKBAR_ACTION_TYPES } from '../../components/AppSnackbarProvider/AppSnackbarProvider';
import logger from '../../services/logger.service';
import i18n from '../../infrastructure/I18n/i18n';
import { History } from '../../components/NavigateSetter';

import {
    CreateNotificationDocument,
    CreateNotificationMutation,
    CreateNotificationMutationVariables,
    DeleteNotificationsDocument,
    DeleteNotificationsMutation,
    DeleteNotificationsMutationVariables,
    GetAudiencesListsDocument,
    GetNotificationByIdDocument,
    GetNotificationByIdQuery,
    GetNotificationByIdQueryVariables,
    GetNotificationsCountDocument,
    GetNotificationsFiltersFieldsDocument,
    Notification as ApolloNotification,
    NotificationInputType,
    UpdateNotificationsDocument,
    UpdateNotificationsMutation,
    UpdateNotificationsMutationVariables
} from '../../../generated/apollo-hooks';
import _omit from 'lodash/omit';
import { Locations } from '../../models/locations';
import { getOperationName } from '@apollo/client/utilities';
import { DocumentNode } from 'graphql';
import { notificationsQueries } from '../../components/NotificationsList/hooks/notifications';
import { AppTableRowAnimateType, ITableData } from '../../components/AppTable/model';

const keyPrefix = 'actions.notificationsList';
const t = (key: string, params?: any) => i18n.t(`${keyPrefix}.${key}`, params) as string;

const _updateNotificationByIdInCache = (notifications: ApolloNotification[]): ThunkAction => async (dispatch, getState, { apolloClient }) => {
    const { currentSystem, currentEnvironment } = getState().common;

    notifications.forEach(savedNotification => {
        if (savedNotification.id && savedNotification.visualDesignData) {
            apolloClient.cache.updateQuery<GetNotificationByIdQuery, GetNotificationByIdQueryVariables>(
                {
                    query: GetNotificationByIdDocument,
                    variables: {
                        notificationId: savedNotification.id,
                        systemId: currentSystem,
                        environmentId: currentEnvironment?.id
                    }
                },
                () => ({
                    __typename: 'Query',
                    getNotificationById: savedNotification
                })
            );
        }
    });
};

export const parseNotificationApollo = (notification: $TSFixMe): NotificationInputType => {
    const parsed = _omit({ ...notification }, 'createdAt', 'updatedAt', 'audiences', 'rowKey', 'activeRecipientsNum', 'allRecipientsNum') as $TSFixMe;

    if (parsed?.user) {
        parsed.user = _omit(notification.user, 'role');
    }

    if (!parsed.endUsersIds) {
        parsed.endUsersIds = null;
    }

    delete parsed.isAllAudienceSelected;

    return parsed;
};

export const triggerRowAnimationAction = ({
    tableKey,
    animateType,
    ids
}: {
    tableKey: $TSFixMe;
    animateType: AppTableRowAnimateType;
    ids: string[];
}): ThunkAction => (dispatch, getState) => {
    const activeTab = getState().notifications.activeTab;
    const { pathname } = History.location()

    if (pathname === Locations.Root && TabsConfig?.[activeTab]?.tableKey === tableKey) {
        return triggerRowAnimation({
            animateType,
            ids,
            getValue: () => getState().notifications.tableStates[tableKey].animateIds,
            onChange: animateIds => dispatch(setTableState(tableKey, { animateIds }))
        });
    }

    return null;
};

export const setDrawer = (drawer: Partial<IDrawer>) => ({
    type: notificationsListActions.SET_DRAWER,
    payload: drawer
});

export const setDisplayedNotification = (notification: Notification | null): ThunkAction => (dispatch, getState) => {
    const { notifications } = getState();

    const notificationId = notifications.drawer?.data?.notificationId;

    // @ts-expect-error ts-migrate(2531) FIXME: Object is possibly 'null'.
    if (notificationId === notification.id) {
        return dispatch(setDrawer({ mode: DrawerMode.Insights, data: null }));
    }

    return dispatch(
        setDrawer({
            mode: DrawerMode.Notification,
            smallScreenHidden: false,
            data: { notificationId: notification?.id }
        })
    );
};

export const pauseNotifications = (notifications: Notification[]): ThunkAction => async (dispatch, getState) => {
    const approved = await confirmDialog({
        dataTestId: 'pause-notification-dialog',
        confirmText: t('pauseNotifications.pause'),
        title: t('pauseNotifications.title', { count: notifications.length })
    });

    if (!approved) {
        return;
    }

    dispatch(addProcessingRowIds(TableKeys.ACTIVE, notifications));

    const { currentEnvironment } = getState().common;

    await dispatch(
        saveMultipleNotificationsPauseState(
            notifications,
            NotificationPauseState.Pause,
            currentEnvironment.id,
            t('pauseNotifications.successMessage', { count: notifications.length }),
            t('pauseNotifications.errorMessage')
        )
    );

    dispatch(removeProcessingRowIds(TableKeys.ACTIVE, notifications));
};

export const resumeNotifications = (notifications: Notification[]): ThunkAction => async (dispatch, getState) => {
    const { environments, currentEnvironment, currentSystem } = getState().common;

    return await publishNotificationsConfirm({
        environments,
        currentEnvironmentId: currentEnvironment.id,
        systemId: currentSystem,
        notifications,
        isResuming: true
    } as $TSFixMe);
};

export const restoreNotifications = (notifications: Notification[]): ThunkAction => async (dispatch, getState) => {
    const approved = await confirmDialog({
        dataTestId: 'restore-notification-dialog',
        confirmText: t('restoreNotifications.restore'),
        title: t('restoreNotifications.title', { count: notifications.length })
    });

    if (!approved) {
        return;
    }

    dispatch(addProcessingRowIds(TableKeys.ARCHIVED, notifications));

    const { currentEnvironment } = getState().common;

    const updatedNotifications = notifications.map(notification => ({
        ...notification,
        publishStates: notification?.publishStates?.map?.(ps => {
            if (ps.environmentId === currentEnvironment.id) {
                return {
                    ...ps,
                    publishStatus: ps?.publishedAt ? NotificationPublishStatus.Published : NotificationPublishStatus.Draft
                };
            }
            return ps;
        })
    }));

    const allSent = updatedNotifications.every(notification => isNotificationOld(notification, currentEnvironment.id));
    const allDraft = updatedNotifications.every(notification => isDraftNotification(notification, currentEnvironment.id));
    const restoredTo =
        allSent || allDraft
            ? t('restoreNotifications.tabName', {
                  tabKey: allSent ? TableKeys.SENT : TableKeys.DRAFTS
              })
            : '';
    const refetchTables = allSent ? [TableKeys.SENT] : allDraft ? [TableKeys.DRAFTS] : [TableKeys.SENT, TableKeys.DRAFTS];

    try {
        await dispatch(
            _updateNotificationMutation(updatedNotifications, currentEnvironment?.id, [TableKeys.ARCHIVED, ...refetchTables], {
                animate: { tableKey: TableKeys.ARCHIVED, animateType: AppTableRowAnimateType.modified }
            })
        );

        dispatch(
            enqueueSnackbar({
                message: t('restoreNotifications.successMessage', {
                    count: notifications.length,
                    context: restoredTo ? 'restoredTo' : undefined,
                    tabName: restoredTo
                }),
                options: { variant: 'success' }
            })
        );
    } catch (error) {
        enqueueSnackbar({
            message: t('restoreNotifications.errorMessage', { count: notifications.length }),
            options: { variant: 'error' }
        });
        logger.error('restoreNotifications error', error);
    } finally {
        dispatch(removeProcessingRowIds(TableKeys.ARCHIVED, notifications));
    }
};

export const deleteNotifications = (notifications: Notification[], withConfirmation = true, tableKey = TableKeys.ARCHIVED): ThunkAction => async (
    dispatch,
    getState,
    { apolloClient }
) => {
    const { currentSystem } = getState().common;
    if (withConfirmation) {
        const approved = await confirmDelete({
            count: notifications.length,
            permanently: true,
            itemName: 'notification'
        });

        if (!approved) {
            return;
        }
    }

    dispatch(addProcessingRowIds(tableKey, notifications));

    try {
        const { data } = await apolloClient.mutate<DeleteNotificationsMutation, DeleteNotificationsMutationVariables>({
            mutation: DeleteNotificationsDocument,
            refetchQueries: [GetAudiencesListsDocument],
            variables: {
                systemId: currentSystem,
                ids: _map<Notification>(notifications, 'id')
            }
        });

        await dispatch(
            triggerRowAnimationAction({
                ids: data?.deleteNotifications?.deletedIds || [],
                animateType: AppTableRowAnimateType.deleted,
                tableKey
            })
        );

        await dispatch(_refetchNotificationsTableQueries([tableKey]));

        dispatch(
            enqueueSnackbar({
                message: t('deleteNotifications.successMessage', { count: notifications.length }),
                options: { variant: 'success' }
            })
        );
    } catch (error) {
        dispatch(
            enqueueSnackbar({
                message: t('deleteNotifications.errorMessage', { count: notifications.length }),
                options: { variant: 'error' }
            })
        );
        logger.error('deleteNotifications error', error);
    } finally {
        dispatch(removeProcessingRowIds(tableKey, notifications));
    }
};

export const addProcessingRowIds = (tableKey: TableKeys, notifications: Notification[]): ThunkAction => (dispatch, getState) => {
    const tableData = getState().notifications.tableStates[tableKey];
    const clone = new Map(tableData.animateIds);

    notifications.forEach(notification => {
        if (notification.id) {
            clone.set(notification.id, AppTableRowAnimateType.process);
        }
    });

    dispatch(setTableState(tableKey, { animateIds: clone }));
};

export const removeProcessingRowIds = (tableKey: TableKeys, notifications: Notification[]): ThunkAction => (dispatch, getState) => {
    const tableData = getState().notifications.tableStates[tableKey];
    const clone = new Map(tableData.animateIds);

    notifications.forEach(notification => {
        if (notification.id) {
            if (clone.get(notification?.id) === AppTableRowAnimateType.process) {
                clone.delete(notification?.id);
            }
        }
    });

    dispatch(setTableState(tableKey, { animateIds: clone }));
};

export const moveNotificationsToTrash = (notifications: Notification[], tableKey: TableKeys): ThunkAction => async (dispatch, getState) => {
    const approved = await confirmDelete({ count: notifications.length, itemName: 'notification' });

    if (!approved) {
        return;
    }

    dispatch(addProcessingRowIds(tableKey, notifications));

    const { currentEnvironment } = getState().common;

    const updatedNotifications = notifications.map(notification => {
        return {
            ...notification,
            publishStates: notification?.publishStates?.map(ps =>
                ps.environmentId === currentEnvironment.id
                    ? {
                          ...ps,
                          publishStatus: NotificationPublishStatus.Deleted,
                          ...(ps?.publishStatus === NotificationPublishStatus.Draft
                              ? {}
                              : {
                                    // @ts-expect-error ts-migrate(2769) FIXME: No overload matches this call.
                                    startAt: new Date() < new Date(ps?.startAt) ? new Date() : ps.startAt,
                                    // @ts-expect-error ts-migrate(2769) FIXME: No overload matches this call.
                                    endAt: new Date() < new Date(ps?.endAt) ? new Date() : ps?.endAt
                                })
                      }
                    : ps
            )
        };
    });

    try {
        await dispatch(
            _updateNotificationMutation(updatedNotifications, currentEnvironment.id, [tableKey, TableKeys.ARCHIVED], {
                animate: { tableKey, animateType: AppTableRowAnimateType.deleted }
            })
        );

        dispatch(
            enqueueSnackbar({
                message: t('moveNotificationsToTrash.successMessage', { count: notifications.length }),
                options: { variant: 'success' }
            })
        );
    } catch (error) {
        enqueueSnackbar({
            message: t('moveNotificationsToTrash.errorMessage', { count: notifications.length }),
            options: { variant: 'error' }
        });
        logger.error('moveNotificationsToTrash error', error);
    } finally {
        dispatch(removeProcessingRowIds(tableKey, notifications));
    }
};

export const setSearchNotifications = (searchTerm: string) => ({
    type: notificationsListActions.SEARCH_NOTIFICATONS,
    payload: searchTerm
});

export const setTableState = (key: TableKeys, data: Partial<ITableData>) => ({
    type: notificationsListActions.SET_TABLE_STATE,
    payload: { key, data }
});

export const setActiveTab = (tab: TableKeys) => ({
    type: notificationsListActions.SET_ACTIVE_TAB,
    payload: tab
});

export const setAllNotificationsView = (isAllNotificationsView: boolean) => ({
    type: notificationsListActions.SET_ALL_NOTIFICATIONS_VIEW,
    payload: isAllNotificationsView
});

const _refetchNotificationsTableQueries = (keys: TableKeys[]): ThunkAction => (_, __, { apolloClient }) => {
    const refetchQueries: DocumentNode[] = [];

    keys.forEach(key => refetchQueries.push(notificationsQueries[key].doc));

    const operations = refetchQueries.map(doc => getOperationName(doc));

    operations.forEach(operation => {
        if (operation) {
            apolloClient.cache.evict({ id: 'ROOT_QUERY', fieldName: operation });
        }
    });

    apolloClient.cache.evict({ id: 'ROOT_QUERY', fieldName: getOperationName(GetNotificationsCountDocument) as string });
    apolloClient.cache.evict({ id: 'ROOT_QUERY', fieldName: getOperationName(GetNotificationsFiltersFieldsDocument) as string });

    return apolloClient.refetchQueries({ include: refetchQueries });
};

const _updateNotificationMutation = (
    updatedNotifications: Notification[],
    environmentId: string,
    refetchTables: TableKeys[] | null,
    {
        changeTab,
        animate,
        skipPublish
    }: {
        changeTab?: TableKeys;
        animate?: {
            tableKey: TableKeys;
            animateType: AppTableRowAnimateType;
        };
        skipPublish?: boolean;
    }
): ThunkAction => async (dispatch, getState, { apolloClient }) => {
    const { currentSystem: systemId } = getState().common;

    const notificationsResults =
        updatedNotifications.length === 1 && !updatedNotifications[0]?.id
            ? (
                  await apolloClient.mutate<CreateNotificationMutation, CreateNotificationMutationVariables>({
                      mutation: CreateNotificationDocument,
                      refetchQueries: [GetAudiencesListsDocument],
                      fetchPolicy: 'no-cache',
                      variables: {
                          systemId,
                          environmentId,
                          notification: parseNotificationApollo(updatedNotifications?.[0]),
                          skipPublish
                      }
                  })
              )?.data?.createNotification.notifications
            : (
                  await apolloClient.mutate<UpdateNotificationsMutation, UpdateNotificationsMutationVariables>({
                      mutation: UpdateNotificationsDocument,
                      refetchQueries: [GetAudiencesListsDocument],
                      fetchPolicy: 'no-cache',
                      variables: {
                          systemId,
                          environmentId,
                          notifications: updatedNotifications.map(notification => parseNotificationApollo(notification)),
                          skipPublish
                      }
                  })
              )?.data?.updateNotifications.notifications;

    // Fix for https://walkme.atlassian.net/browse/WSCN-1595
    apolloClient.cache.evict({ id: 'ROOT_QUERY', fieldName: 'getAudience' });

    if (notificationsResults) {
        dispatch(_updateNotificationByIdInCache(notificationsResults as ApolloNotification[]));
    }

    const updateApolloRequest = () => (refetchTables ? dispatch(_refetchNotificationsTableQueries(refetchTables)) : null);
    const animateRequest = () => (animate ? dispatch(triggerRowAnimationAction({ ids: _map(notificationsResults, 'id'), ...animate })) : null);

    if (changeTab) {
        await updateApolloRequest();

        dispatch(setActiveTab(changeTab));
        History.navigate()(Locations.Root);

        animateRequest();
        return notificationsResults;
    }

    await animateRequest();

    await updateApolloRequest();

    return notificationsResults;
};

export const markNotificationsAsDone = (notifications: Notification[], tableKey: TableKeys): ThunkAction => async (dispatch, getState) => {
    const state = getState();
    const { currentEnvironment } = state.common;

    dispatch(addProcessingRowIds(tableKey, notifications));

    const updatedNotifications = notifications.map(notification => {
        const isScheduled = isNotificationScheduled(notification, currentEnvironment.id);

        return {
            ...notification,
            publishStates: notification?.publishStates?.map(ps =>
                ps.environmentId === currentEnvironment.id
                    ? {
                          ...ps,
                          startAt: isScheduled ? new Date() : ps.startAt,
                          endAt: new Date()
                      }
                    : ps
            )
        };
    });

    try {
        await dispatch(
            _updateNotificationMutation(updatedNotifications, currentEnvironment.id, [tableKey, TableKeys.SENT], {
                animate: { tableKey, animateType: AppTableRowAnimateType.modified }
            })
        );

        dispatch(
            enqueueSnackbar({
                message: t('markNotificationsAsDone.successMessage', { count: notifications.length }),
                options: { variant: 'success' }
            })
        );
    } catch (e) {
        dispatch(enqueueSnackbar({ message: t('markNotificationsAsDone.errorMessage'), options: { variant: 'error' } }));
    } finally {
        dispatch(removeProcessingRowIds(tableKey, notifications));
    }
};

export const saveNotification = ({
    notification,
    tableKey = TableKeys.DRAFTS,
    environmentId,
    successMessage,
    errorMessage,
    skipPublish
}: {
    notification: Notification;
    tableKey?: TableKeys;
    environmentId?: UniqueId;
    successMessage?: string;
    errorMessage?: string;
    skipPublish?: boolean;
}): ThunkAction => async (dispatch, getState) => {
    const { currentEnvironment } = getState().common;
    const { activeTab } = getState().notifications;

    const targetEnvironmentId = environmentId || currentEnvironment.id;
    const refetchTables = [tableKey];

    if (notification.id && activeTab !== tableKey) {
        refetchTables.push(activeTab);
    }

    try {
        const notificationsResults = await dispatch(
            _updateNotificationMutation([notification], targetEnvironmentId, refetchTables, {
                changeTab: tableKey,
                animate: {
                    tableKey: tableKey,
                    animateType: AppTableRowAnimateType.created
                },
                skipPublish
            }) as $TSFixMe
        );

        dispatch(
            enqueueSnackbar({
                message: successMessage || t('saveNotification.successMessage'),
                action:
                    tableKey === TableKeys.DRAFTS
                        ? {
                              callback: () => {
                                  dispatch(deleteNotifications(notificationsResults, false, TableKeys.DRAFTS) as $TSFixMe);
                              },
                              type: SNACKBAR_ACTION_TYPES.undo
                          }
                        : undefined,
                options: { variant: 'success' }
            })
        );
    } catch (error) {
        dispatch(
            enqueueSnackbar({
                message: errorMessage || t('saveNotification.errorMessage'),
                options: { variant: 'error' }
            })
        );
    }
};

export const saveMultipleNotificationsPauseState = (
    notifications: Notification[],
    newNotificationPauseState: NotificationPauseState,
    environmentId: UniqueId,
    successMessage: string,
    errorMessage: string
): ThunkAction => async dispatch => {
    const updatedNotifications = notifications.map(notification => ({
        ...notification,
        // @ts-expect-error ts-migrate(2532) FIXME: Object is possibly 'undefined'.
        publishStates: notification.publishStates.map(ps =>
            ps.environmentId === environmentId
                ? {
                      ...ps,
                      publishStatus:
                          newNotificationPauseState === NotificationPauseState.Pause
                              ? NotificationPublishStatus.Paused
                              : NotificationPublishStatus.Published
                  }
                : ps
        )
    }));

    try {
        await dispatch(
            _updateNotificationMutation(updatedNotifications, environmentId, [TableKeys.ACTIVE], {
                animate: { tableKey: TableKeys.ACTIVE, animateType: AppTableRowAnimateType.created }
            })
        );

        dispatch(
            enqueueSnackbar({
                message: successMessage,
                options: { variant: 'success' }
            })
        );
    } catch (error) {
        dispatch(
            enqueueSnackbar({
                message: errorMessage,
                options: { variant: 'error' }
            })
        );
    }
};

export const publishSingleNotification = (
    notification: Notification,
    environmentId: string,
    { isResuming, isPausing }: { isResuming: boolean; isPausing: boolean }
): ThunkAction => async (dispatch, getState) => {
    const currentEnvironment = getState().common.currentEnvironment;
    const activeTab = getState().notifications.activeTab;

    const originalPublishState = getNotificationPublishState(notification, environmentId);

    const newPublishState: IPublishState = {
        ...originalPublishState,
        environmentId: environmentId
    };

    newPublishState.publishStatus = isPausing ? NotificationPublishStatus.Paused : NotificationPublishStatus.Published;

    setNotificationPublishState(notification, newPublishState);

    const isScheduled = isNotificationScheduled(notification, environmentId);

    let tableKey = isScheduled ? TableKeys.SCHEDULED : TableKeys.ACTIVE;

    if (currentEnvironment?.id !== environmentId) {
        if (notification.id) {
            tableKey = activeTab;
        } else {
            tableKey = TableKeys.DRAFTS;
        }
    }

    return dispatch(
        saveNotification({
            notification,
            tableKey,
            environmentId,
            successMessage: t('publishSingleNotification.successMessage', {
                context: isResuming ? 'resumed' : isScheduled ? 'scheduled' : 'activated'
            }),
            errorMessage: t('publishSingleNotification.errorMessage')
        })
    );
};

export const updateNotificationNonRuntimeFields = (notification: Notification): ThunkAction => async (dispatch, getState) => {
    try {
        const currentEnvironment = getState().common.currentEnvironment;
        await dispatch(_updateNotificationMutation([notification], currentEnvironment?.id, null, { skipPublish: true }));

        dispatch(
            enqueueSnackbar({
                message: t('updateNotificationNonRuntimeFields.successMessage'),
                options: { variant: 'success' }
            })
        );
    } catch (error) {
        dispatch(
            enqueueSnackbar({
                message: t('updateNotificationNonRuntimeFields.errorMessage'),
                options: { variant: 'error' }
            })
        );
    }
};
